import {createContext, use, useCallback, useMemo, useState} from 'react'
import type {CommandType, LogEntry} from '../types/session'
import {isCompletionToolMessage, isToolOutputsMessage} from '../types/session'
import {useLogsQuery} from '../hooks/useLogs'
import {useSessionEvents} from '../hooks/use-session-events'
import {uuid} from '../utils/uuid'
import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * Represents an optimistic prompt response that will be shown
 * in the logs before the server confirms it.
 */
export interface OptimisticPromptResponse {
  /** Unique ID for this optimistic entry */
  id: string
  /** The tool call ID this response is for */
  toolCallId: string
  /** The response content */
  response: unknown
  /** The command type being responded to */
  commandType: CommandType
  /** When this was created */
  createdAt: string
  /** Current status */
  status: 'pending' | 'confirmed' | 'failed'
  /**
   * True for entries created by client-side fan-out rather than a direct
   * user click — e.g. when a scope=session approval propagates to other
   * pending prompts for the same MCP server/tool.
   */
  inferred?: boolean
  /**
   * True when this response was triggered while the session was paused
   * (`waiting_for_user`). The "Resuming session…" indicator only renders
   * for paused-session clicks; CLI sessions don't pause.
   */
  fromPausedSession?: boolean
}

interface OptimisticLogsContextValue {
  /** All current optimistic prompt responses */
  optimisticResponses: OptimisticPromptResponse[]
  /** Add an optimistic prompt response. Returns the ID of the created entry. */
  addOptimisticResponse: (params: {
    toolCallId: string
    response: unknown
    commandType: CommandType
    inferred?: boolean
    fromPausedSession?: boolean
  }) => string
  /**
   * Mark an optimistic response as failed and remove it.
   */
  removeOptimisticResponse: (id: string) => void
  /**
   * Get the optimistic response for a specific tool call, if any.
   */
  getOptimisticResponseForToolCall: (toolCallId: string) => OptimisticPromptResponse | undefined
}

export const OptimisticLogsContext = createContext<OptimisticLogsContextValue | undefined>(undefined)

interface OptimisticLogsProviderProps {
  children: React.ReactNode
  /** Session ID to fetch logs for auto-cleanup of optimistic entries (logs mode) */
  sessionId: string | undefined
  /** Task ID for events-mode reconciliation via useSessionEvents */
  taskId?: string | undefined
}

/**
 * Build a Set of tool call IDs that have server-confirmed responses in logs.
 */
function buildResolvedIdsFromLogs(logs: LogEntry[]): Set<string> {
  const ids = new Set<string>()
  for (const entry of logs) {
    if (isCompletionToolMessage(entry)) {
      ids.add(entry.tool_call_id)
    } else if (isToolOutputsMessage(entry)) {
      ids.add(entry.toolCallId)
    }
  }
  return ids
}

/**
 * Provider for managing optimistic log entries.
 *
 * When a user responds to a prompt (permission, ask_user, plan approval),
 * we show the response optimistically before the server confirms it.
 * This prevents a jarring gap between the user's action and visual feedback.
 *
 * Reconciliation (auto-cleanup) works in two modes:
 * - Logs mode: scans server logs for matching tool responses
 * - Events mode: uses `serverResolvedIds` from the conversation model
 */
export function OptimisticLogsProvider({children, sessionId, taskId}: OptimisticLogsProviderProps) {
  const [rawOptimisticResponses, setRawOptimisticResponses] = useState<OptimisticPromptResponse[]>([])
  const useEventsUI = isFeatureEnabled('copilot_mission_control_session_events_ui')

  // Logs-mode reconciliation source
  const {data: logs} = useLogsQuery({
    sessionId,
    enabled: !!sessionId && !useEventsUI,
  })

  // Events-mode reconciliation source (React Query dedupes with other useSessionEvents consumers)
  const {data: eventsData} = useSessionEvents({
    taskId,
    enabled: useEventsUI && !!taskId,
  })

  // Build the unified set of server-resolved tool call IDs
  const serverResolvedToolCallIds = useMemo(() => {
    if (useEventsUI) {
      return eventsData?.model.serverResolvedIds ?? new Set<string>()
    }
    if (logs && logs.length > 0) {
      return buildResolvedIdsFromLogs(logs)
    }
    return new Set<string>()
  }, [useEventsUI, eventsData?.model.serverResolvedIds, logs])

  // Filter out entries that the server has confirmed
  const optimisticResponses = useMemo(() => {
    if (serverResolvedToolCallIds.size === 0 || rawOptimisticResponses.length === 0) {
      return rawOptimisticResponses
    }
    return rawOptimisticResponses.filter(entry => !serverResolvedToolCallIds.has(entry.toolCallId))
  }, [serverResolvedToolCallIds, rawOptimisticResponses])

  const addOptimisticResponse = useCallback(
    ({
      toolCallId,
      response,
      commandType,
      inferred,
      fromPausedSession,
    }: {
      toolCallId: string
      response: unknown
      commandType: CommandType
      inferred?: boolean
      fromPausedSession?: boolean
    }) => {
      const id = `optimistic-${uuid()}`
      const entry: OptimisticPromptResponse = {
        id,
        toolCallId,
        response,
        commandType,
        createdAt: new Date().toISOString(),
        status: 'pending',
        inferred,
        fromPausedSession,
      }
      setRawOptimisticResponses(prev => [...prev, entry])
      return id
    },
    [],
  )

  const removeOptimisticResponse = useCallback((id: string) => {
    setRawOptimisticResponses(prev => prev.filter(r => r.id !== id))
  }, [])

  const getOptimisticResponseForToolCall = useCallback(
    (toolCallId: string) => {
      return optimisticResponses.find(r => r.toolCallId === toolCallId)
    },
    [optimisticResponses],
  )

  const value = useMemo(
    () => ({
      optimisticResponses,
      addOptimisticResponse,
      removeOptimisticResponse,
      getOptimisticResponseForToolCall,
    }),
    [optimisticResponses, addOptimisticResponse, removeOptimisticResponse, getOptimisticResponseForToolCall],
  )

  return <OptimisticLogsContext value={value}>{children}</OptimisticLogsContext>
}

export function useOptimisticLogs(): OptimisticLogsContextValue {
  const context = use(OptimisticLogsContext)
  if (!context) {
    throw new Error('useOptimisticLogs must be used within an OptimisticLogsProvider')
  }
  return context
}

/**
 * Hook that safely returns optimistic logs context if available.
 * Returns undefined if not within provider (for gradual adoption).
 */
export function useOptimisticLogsOptional(): OptimisticLogsContextValue | undefined {
  return use(OptimisticLogsContext)
}
